// FCM web push — client half. The server half (device-token registry +
// sends on friend request/accept, plan invite/finalize/cancel, RSVP→host,
// shares, new chat messages) already exists in nudge-api.
//
// Requires VITE_FIREBASE_VAPID_KEY (Firebase console → Cloud Messaging →
// Web push certificates). Without it, everything here quietly no-ops so
// the app works before push is configured.

import { apiRegisterDeviceToken, apiRemoveDeviceToken } from './api.js';

const VAPID_KEY = import.meta.env.VITE_FIREBASE_VAPID_KEY;
const TOKEN_LS_KEY = 'larkle-fcm-token';

function supported() {
  return !!VAPID_KEY
    && typeof Notification !== 'undefined'
    && 'serviceWorker' in navigator;
}

// Ask permission (must be called from a user gesture), fetch an FCM token,
// register it with the API. Safe to call repeatedly — re-registration
// refreshes the server-side record.
export async function enablePush(app, onMessageCb) {
  if (!supported()) return { ok: false, reason: 'unsupported' };
  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return { ok: false, reason: 'denied' };

  const { getMessaging, getToken, onMessage } = await import('firebase/messaging');
  const messaging = getMessaging(app);
  const registration = await navigator.serviceWorker.register('/push-sw.js');
  const token = await getToken(messaging, { vapidKey: VAPID_KEY, serviceWorkerRegistration: registration });
  if (!token) return { ok: false, reason: 'no-token' };

  await apiRegisterDeviceToken(token, 'Web');
  try { localStorage.setItem(TOKEN_LS_KEY, token); } catch {}

  // Foreground messages don't hit the service worker — surface them in-app.
  if (onMessageCb) onMessage(messaging, onMessageCb);
  return { ok: true };
}

// Called on sign-out: tell the server to stop pushing to this device.
export async function disablePush() {
  let token = null;
  try { token = localStorage.getItem(TOKEN_LS_KEY); } catch {}
  if (!token) return;
  try { localStorage.removeItem(TOKEN_LS_KEY); } catch {}
  await apiRemoveDeviceToken(token).catch(() => {});
}

export function pushSupported() { return supported(); }
export function pushEnabled() {
  try { return supported() && Notification.permission === 'granted' && !!localStorage.getItem(TOKEN_LS_KEY); }
  catch { return false; }
}
